import { createStructuredSelector } from 'reselect';
import {
  ChangeEvent,
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState
} from 'react';
import { connect } from 'react-redux';
import { session } from '~/App/shared/selectors/sessionSelector';
import {
  resendEmailVerification,
  verifyMemberEmail as verifyMemberEmailCall
} from '~/helpers/orchestration/verification';
import { verifyMemberEmail } from '~/App/shared/actions/session';
import { Session } from '~/App/shared/interfaces/store/Session';
import { AxiosError } from 'axios';
import { EmailSubmit } from './EmailVerificationForm';
import { ReduxDispatch } from '~/App/shared/interfaces/Redux';

interface Props {
  token: string;
  session: Session;
  verifyMemberEmail: CallableFunction;
  children: (args: EmailSubmit) => ReactElement;
}

const EmailVerificationSubmit = ({
  token,
  session,
  verifyMemberEmail,
  children
}: Props) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isVerified, setVerified] = useState<boolean>(false);
  const [errors, setErrors] = useState<EmailSubmit['errors']>();
  const [resend, setResend] = useState<EmailSubmit['resend']>();

  const handleResendEmail = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (resend === 'loading') {
      return;
    }

    setResend('loading');

    resendEmailVerification({
      idToken: session.auth.idToken
    })
      .then(() => {
        setResend('success');
      })
      .catch(() => {
        setResend('error');
      });
  };

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      if (event) {
        event.preventDefault();
      }

      setLoading(true);
      setVerified(false);

      verifyMemberEmailCall(token)
        .then(() => {
          setLoading(false);
          setVerified(true);

          verifyMemberEmail();
        })
        .catch((error: AxiosError) => {
          const is400 =
            error && error.response && error.response.status === 400;
          const errData = is400
            ? {
                status: 400,
                message: 'INVALID_TOKEN'
              }
            : {
                status: 500,
                message: 'UNKNOWN_ERROR'
              };

          setErrors([errData]);
          setLoading(false);
          setVerified(false);
        });
    },
    [token, verifyMemberEmail]
  );

  useEffect(() => {
    handleSubmit();
  }, [handleSubmit]);

  return children({
    resend: resend,
    errors: errors,
    isLoading: isLoading,
    isVerified: isVerified,
    handleResendEmail: handleResendEmail,
    handleSubmit: handleSubmit
  });
};

const mapStateToProps = createStructuredSelector({
  session: session
});

const mapDispatchToProps = (dispatch: ReduxDispatch) => ({
  verifyMemberEmail: () => dispatch(verifyMemberEmail())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmailVerificationSubmit);
